import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Popover,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Datagrid,
  DateField,
  ExportButton,
  Filter,
  FilterButton,
  List,
  TextField,
  TextInput,
  TopToolbar,
  useListContext,
} from "react-admin";
import XLSX from "xlsx";
import { parentActive, parentReadMany, prefetchParents, getFilteredParents } from "../../provider/provider";

const CalendarIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ─── Date Range Filter Component
const PRESETS = [
  { label: "Last 15 Days", days: 15 },
  { label: "Last 30 Days", days: 30 },
  { label: "Last 60 Days", days: 60 },
  { label: "Last 90 Days", days: 90 },
];

const DateRangeFilter = ({ dateFilter, onApply, onClear }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    if (dateFilter) {
      setFromDate(dateFilter.from || "");
      setToDate(dateFilter.to || "");
      const matchedPreset = PRESETS.find((p) => {
        const presetFrom = dayjs().subtract(p.days, "day").format("YYYY-MM-DD");
        return dateFilter.from === presetFrom;
      });
      setSelectedPreset(matchedPreset ? matchedPreset.days : null);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePresetClick = (days) => {
    const from = dayjs().subtract(days, "day").format("YYYY-MM-DD");
    const to = dayjs().format("YYYY-MM-DD");
    setSelectedPreset(days);
    setFromDate(from);
    setToDate(to);
  };

  const handleApply = () => {
    if (fromDate && toDate) {
      onApply({ from: fromDate, to: toDate });
      handleClose();
    }
  };

  const handleClear = () => {
    setSelectedPreset(null);
    setFromDate("");
    setToDate("");
    onClear();
    handleClose();
  };

  // Build a readable label for the active filter chip
  const activeLabel = useMemo(() => {
    if (!dateFilter) return null;
    const matchedPreset = PRESETS.find((p) => {
      const presetFrom = dayjs().subtract(p.days, "day").format("YYYY-MM-DD");
      return dateFilter.from === presetFrom;
    });
    if (matchedPreset) return matchedPreset.label;
    const from = dayjs(dateFilter.from).format("MMM D, YYYY");
    const to = dayjs(dateFilter.to).format("MMM D, YYYY");
    return `${from} – ${to}`;
  }, [dateFilter]);

  const isApplyDisabled = !fromDate || !toDate;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {/* Trigger Button */}
      <Button
        variant={dateFilter ? "contained" : "outlined"}
        size="small"
        onClick={handleOpen}
        startIcon={<CalendarIcon />}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.8125rem",
          borderRadius: "8px",
          px: 2,
          py: 0.75,
          borderColor: dateFilter ? "primary.main" : "#c4c4c4",
          color: dateFilter ? "#fff" : "#555",
          boxShadow: dateFilter
            ? "0 2px 8px rgba(60, 77, 68, 0.25)"
            : "none",
          "&:hover": {
            borderColor: "primary.main",
            backgroundColor: dateFilter ? "primary.dark" : "rgba(60, 77, 68, 0.04)",
          },
          transition: "all 0.2s ease",
        }}
      >
        {dateFilter ? "Date Filtered" : "Date Filter"}
      </Button>
      {dateFilter && activeLabel && (
        <Chip
          label={activeLabel}
          size="small"
          onDelete={handleClear}
          deleteIcon={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 18,
                height: 18,
                borderRadius: "50%",
                backgroundColor: "rgba(0,0,0,0.08)",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.15)" },
                transition: "background 0.15s",
              }}
            >
              <CloseIcon />
            </Box>
          }
          sx={{
            fontWeight: 500,
            fontSize: "0.75rem",
            backgroundColor: "#e8f5e9",
            color: "#2e7d32",
            border: "1px solid #a5d6a7",
            borderRadius: "6px",
            "& .MuiChip-deleteIcon": {
              color: "#555",
            },
          }}
        />
      )}

      {/* Popover Dropdown */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)",
              overflow: "visible",
              border: "1px solid rgba(0,0,0,0.06)",
            },
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: 340,
            p: 0,
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2.5,
              py: 1.75,
              background: "linear-gradient(135deg, #3C4D44 0%, #506b5c 100%)",
              color: "#fff",
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: "0.875rem", letterSpacing: "0.02em" }}
            >
              Filter by Created Date
            </Typography>
            <Typography sx={{ fontSize: "0.7rem", opacity: 0.8, mt: 0.25 }}>
              Select a preset or choose custom dates
            </Typography>
          </Box>

          {/* Quick Presets */}
          <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
            <Typography
              sx={{
                fontSize: "0.6875rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "#999",
                mb: 1,
              }}
            >
              Quick Select
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {PRESETS.map((preset) => (
                <Chip
                  key={preset.days}
                  label={preset.label}
                  size="small"
                  variant={selectedPreset === preset.days ? "filled" : "outlined"}
                  onClick={() => handlePresetClick(preset.days)}
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    ...(selectedPreset === preset.days
                      ? {
                        backgroundColor: "#3C4D44",
                        color: "#fff",
                        borderColor: "#3C4D44",
                        "&:hover": { backgroundColor: "#506b5c" },
                      }
                      : {
                        borderColor: "#ddd",
                        color: "#555",
                        "&:hover": {
                          borderColor: "#3C4D44",
                          backgroundColor: "rgba(60, 77, 68, 0.04)",
                        },
                      }),
                  }}
                />
              ))}
            </Box>
          </Box>

          <Divider sx={{ mx: 2.5, my: 1.5, borderColor: "rgba(0,0,0,0.06)" }} />

          {/* Custom Date Range */}
          <Box sx={{ px: 2.5, pb: 2 }}>
            <Typography
              sx={{
                fontSize: "0.6875rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "#999",
                mb: 1.25,
              }}
            >
              Custom Range
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <MuiTextField
                label="From"
                type="date"
                size="small"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setSelectedPreset(null);
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: toDate || dayjs().format("YYYY-MM-DD") }}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontSize: "0.8125rem",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#3C4D44",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#3C4D44",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#3C4D44",
                  },
                }}
              />
              <MuiTextField
                label="To"
                type="date"
                size="small"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setSelectedPreset(null);
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: fromDate,
                  max: dayjs().format("YYYY-MM-DD"),
                }}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontSize: "0.8125rem",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#3C4D44",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#3C4D44",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#3C4D44",
                  },
                }}
              />
            </Box>
          </Box>

          {/* Actions */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              px: 2.5,
              py: 1.75,
              backgroundColor: "#fafafa",
              borderTop: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <Button
              size="small"
              onClick={handleClear}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8125rem",
                color: "#888",
                borderRadius: "8px",
                px: 2,
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                  color: "#555",
                },
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleApply}
              disabled={isApplyDisabled}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8125rem",
                borderRadius: "8px",
                px: 2.5,
                backgroundColor: "#3C4D44",
                boxShadow: "0 2px 8px rgba(60, 77, 68, 0.3)",
                "&:hover": {
                  backgroundColor: "#506b5c",
                  boxShadow: "0 4px 12px rgba(60, 77, 68, 0.35)",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#c4c4c4",
                  color: "#fff",
                },
                transition: "all 0.2s ease",
              }}
            >
              Apply Filter
            </Button>
          </Box>
        </Paper>
      </Popover>
    </Box>
  );
};

// ─── Parent Filter (existing search filters) ────────────────────────────────
const ParentFilter = (props) => {
  const [value, setValue] = React.useState("");

  useEffect(() => {
    if (value && value.trim().length > 0) {
      props?.setValue(value.split(",").map((it) => it.trim()));
    } else {
      props?.setValue([]);
    }
  }, [value]);

  return (
    <Filter {...props}>
      {/* <TextInput label="Search by Id" source="id" alwaysOn /> */}
      <TextInput label="Search by Email" source="email" alwaysOn />
      <TextInput label="Search by Mobile No" source="mobile" alwaysOn />

      <MuiTextField
        value={value}
        onChange={(event) => {
          setValue(event.target.value);
        }}
        alwaysOn
        label="Parent Ids"
        multiline
        fullWidth
        sx={{ width: "400px" }}
      />
    </Filter>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
function formatIndianMobile(mobileNumber) {
  let cleaned = mobileNumber.replace(/\D/g, "");

  if (cleaned.startsWith("0")) {
    cleaned = cleaned.slice(1); // Remove the leading 0
  }

  // Check if the number is a valid Indian mobile number
  if (cleaned.length === 10) {
    // If it's a 10-digit number, add +91
    return "+91" + cleaned;
  } else if (cleaned.length === 12 && cleaned.startsWith("91")) {
    // If it's already a 12-digit number starting with 91, add +
    return "+" + cleaned;
  } else if (cleaned.length === 13 && cleaned.startsWith("+91")) {
    // If it already has the +91 prefix, return as is
    return cleaned;
  } else {
    return;
  }
}

export async function ParentExporter() {
  const users = await parentReadMany("", "", true);
  console.log(users);
  const parsedRows = users.data.map((it) => {
    return {
      Id: it?.id ?? "",
      Name: it?.name ?? "",
      "Mobile Number": formatIndianMobile(it?.mobile ?? ""),
      Email: it?.email ?? "",
      SignupDate: dayjs(it?.createdDate).format("DD-MM-YYYY hh:mm a"),
    };
  });
  const worksheet = XLSX.utils.json_to_sheet(parsedRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
  XLSX.writeFile(workbook, "UserExport.xlsx");
}

function exportToExcel(data, filename) {
  const parsedRows = data.map((it) => ({
    Id: it?.id ?? "",
    Name: it?.name ?? "",
    "Mobile Number": formatIndianMobile(it?.mobile ?? ""),
    Email: it?.email ?? "",
    SignupDate: dayjs(it?.createdDate).format("DD-MM-YYYY hh:mm a"),
  }));
  const worksheet = XLSX.utils.json_to_sheet(parsedRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
  XLSX.writeFile(workbook, filename);
}

const FilteredExportButton = ({ combinedFilter }) => {
  const [exporting, setExporting] = useState(false);
  const { filterValues } = useListContext();

  const mergedFilter = useMemo(() => ({
    ...combinedFilter,
    ...(filterValues?.email ? { email: filterValues.email } : {}),
    ...(filterValues?.mobile ? { mobile: filterValues.mobile } : {}),
  }), [combinedFilter, filterValues]);

  const hasActiveFilter =
    mergedFilter.email ||
    mergedFilter.mobile ||
    (mergedFilter.id && mergedFilter.id.length > 0) ||
    mergedFilter.createdDateFrom ||
    mergedFilter.createdDateTo;

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      if (hasActiveFilter) {
        const result = await getFilteredParents(mergedFilter);
        exportToExcel(result.data, "FilteredParentExport.xlsx");
      } else {
        const result = await parentReadMany("", "", true);
        exportToExcel(result.data, "UserExport.xlsx");
      }
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  }, [mergedFilter, hasActiveFilter]);

  return (
    <Button
      variant="text"
      size="small"
      onClick={handleExport}
      disabled={exporting}
      startIcon={
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      }
      sx={{
        textTransform: "none",
        fontWeight: 600,
        fontSize: "0.8125rem",
        color: hasActiveFilter ? "#2e7d32" : "#555",
      }}
    >
      {exporting
        ? "Exporting..."
        : hasActiveFilter
          ? "Export Filtered"
          : "Export All"}
    </Button>
  );
};

export async function ParentExporterActive() {
  const users = await parentActive();
  console.log(users);
  const parsedRows = users.data.map((it) => {
    return {
      Id: it?.id ?? "",
      Name: it?.name ?? "",
      "Mobile Number": formatIndianMobile(it?.mobile ?? ""),
      Email: it?.email ?? "",
      SignupDate: dayjs(it?.createdDate).format("DD-MM-YYYY hh:mm a"),
    };
  });
  const worksheet = XLSX.utils.json_to_sheet(parsedRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
  XLSX.writeFile(workbook, "UserExport.xlsx");
}

export const ParentList = (props) => {
  const [value, setValue] = React.useState([]);
  const [dateFilter, setDateFilter] = useState(null); // { from: "YYYY-MM-DD", to: "YYYY-MM-DD" }

  console.log("value", value);

  useEffect(() => {
    prefetchParents();
  }, []);
  const combinedFilter = useMemo(() => {
    const filter = {};
    if (value && value.length > 0) {
      filter.id = value;
    }
    if (dateFilter) {
      filter.createdDateFrom = dateFilter.from;
      filter.createdDateTo = dateFilter.to;
    }
    return filter;
  }, [value, dateFilter]);

  return (
    <List
      {...props}
      actions={
        <TopToolbar
          sx={{
            alignItems: "center",
            gap: 1,
          }}
        >
          <DateRangeFilter
            dateFilter={dateFilter}
            onApply={(filter) => setDateFilter(filter)}
            onClear={() => setDateFilter(null)}
          />
          <FilterButton
            filters={[<ParentFilter value={value} setValue={setValue} />]}
          />
          <FilteredExportButton combinedFilter={combinedFilter} />
          <ExportButton exporter={ParentExporterActive} label="Active Export" />
        </TopToolbar>
      }
      filters={<ParentFilter value={value} setValue={setValue} />}
      filter={combinedFilter}
    // aside={<Aside />}
    >
      <Datagrid rowClick="show">
        <TextField source="id" />
        <TextField source="name" />

        <TextField source="email" />
        <TextField source="mobile" />
        <DateField
          source="createdDate"
          options={{ year: "numeric", month: "long", day: "numeric" }}
        />
      </Datagrid>
    </List>
  );
};
